import React, { useRef } from "react";
import SubTaskListView from "./components/SubTaskListView";
import { useSubTaskListController } from "./hooks/useSubTaskListController";
import { GrantDto } from "../../../../dtos/GrantDto";
import { PhaseDto } from "../../../../dtos/PhaseDto";
import { TasklistMode } from "../context/TaskListContext";
import { TasksFeatureFlags } from "../../Types";

// DEBUG: Set to true to show render counters
const DEBUG_RENDERS = false;

export type ISubTaskListProps = {
    getPhaseData: () => Promise<PhaseDto | null>;
    hidePhaseSummary: () => void;
    showPhaseRequestResponseSummary?: (phase: PhaseDto | null) => void;
    deepLinkHash: string | null;
    phaseIsSelected?: boolean;
    onAdding?: () => void;
    onLoading?: () => void;
    onLoaded?: () => void;
    onTaskSelected?: () => void;
    onVisibilityChange?: (visible: boolean) => void;
    onCollapsedChange?: (collapsed: boolean) => void;

    showPhaseSummary: (phase: PhaseDto | null) => void;
    hidePhaseRequestResponseSummary?: () => void;
    summaryButtonVisible?: boolean;
    mode?: TasklistMode
    flags?: TasksFeatureFlags;
    grantData?: GrantDto | null;
    // The caller renders the phase name in its own header, so the toolbar must not repeat it.
    nameShownExternally?: boolean;
    // A newer copy of this phase held by the page around the tasklist, adopted rather than fetched.
    externalPhase?: PhaseDto | null;
};

function SubTaskList(props: ISubTaskListProps) {
    const ctrl = useSubTaskListController(props);

    // DEBUG: Track renders with incrementing counter
    const renderCount = useRef(0);
    renderCount.current += 1;

    if (DEBUG_RENDERS) {
        console.log(`SubTaskList RENDERED - Count: ${renderCount.current}`);
    }

    if (DEBUG_RENDERS) {
        return (
            <div style={{ position: 'relative' }}>
                <div style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    background: 'red',
                    color: 'white',
                    padding: '2px 6px',
                    fontSize: '11px',
                    borderRadius: '3px',
                    zIndex: 9999,
                    fontWeight: 'bold'
                }}>
                    Renders: {renderCount.current}
                </div>
                <SubTaskListView {...ctrl} />
            </div>
        );
    }

    return <SubTaskListView {...ctrl} />;
}

export default React.memo(SubTaskList, (prevProps, nextProps) => {
    // Return true if props are equal (skip render), false if different (re-render)
    const checks = {
        getPhaseData: prevProps.getPhaseData === nextProps.getPhaseData,
        hidePhaseSummary: prevProps.hidePhaseSummary === nextProps.hidePhaseSummary,
        showPhaseSummary: prevProps.showPhaseSummary === nextProps.showPhaseSummary,
        hidePhaseRequestResponseSummary: prevProps.hidePhaseRequestResponseSummary === nextProps.hidePhaseRequestResponseSummary,
        showPhaseRequestResponseSummary: prevProps.showPhaseRequestResponseSummary === nextProps.showPhaseRequestResponseSummary,
        onAdding: prevProps.onAdding === nextProps.onAdding,
        onLoading: prevProps.onLoading === nextProps.onLoading,
        onLoaded: prevProps.onLoaded === nextProps.onLoaded,
        onVisibilityChange: prevProps.onVisibilityChange === nextProps.onVisibilityChange,
        phaseIsSelected: prevProps.phaseIsSelected === nextProps.phaseIsSelected,
        deepLinkHash: prevProps.deepLinkHash === nextProps.deepLinkHash,
        mode: prevProps.mode === nextProps.mode,
        grantData: prevProps.grantData === nextProps.grantData,
        nameShownExternally: prevProps.nameShownExternally === nextProps.nameShownExternally,
    };

    const isEqual = Object.values(checks).every(v => v);

    if (!isEqual && DEBUG_RENDERS) {
        console.log('SubTaskList prop changes:', checks);
    }

    return isEqual;
});